import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { NoteFormat, UpdateNoteInput } from "@ccnote/shared";
import {
  api,
  ApiError,
  json,
  type ApiToken,
  type Attachment,
  type Category,
  type Note,
  type NotesFilter,
  type NotesPage,
  type Sender,
  type XRemark,
  type SessionUser,
  type StorageUsage,
  type Billing,
  type FilesPage,
  type StorageOrder,
} from "./api";

export function useSession() {
  return useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      try {
        return (await api<{ user: SessionUser }>("/auth/session")).user;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 5 * 60_000,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: async () => (await api<{ categories: Category[] }>("/v1/categories")).categories,
  });
}

export function usePendingCount() {
  return useQuery({
    queryKey: ["notes", { status: "pending" }, "count"],
    queryFn: async () => (await api<NotesPage>("/v1/notes?status=pending&limit=100")).notes.length,
  });
}

export function useNotes(filter: NotesFilter) {
  return useInfiniteQuery({
    queryKey: ["notes", filter],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ status: filter.status, limit: "50" });
      if (filter.category) params.set("category", filter.category);
      if (filter.q) params.set("q", filter.q);
      if (pageParam) params.set("cursor", pageParam);
      return api<NotesPage>(`/v1/notes?${params}`);
    },
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useNote(id: string | null) {
  return useQuery({
    queryKey: ["note", id],
    queryFn: () => api<Note>(`/v1/notes/${id}`),
    enabled: Boolean(id),
  });
}

export function useAttachments(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["note", id, "attachments"],
    queryFn: async () => (await api<{ attachments: Attachment[] }>(`/v1/notes/${id}/attachments`)).attachments,
    enabled: Boolean(id) && enabled,
  });
}

/** Anything that changes notes refreshes lists, counts and the open note. */
function useInvalidateNotes() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["notes"] });
    void qc.invalidateQueries({ queryKey: ["note"] });
    void qc.invalidateQueries({ queryKey: ["categories"] });
    void qc.invalidateQueries({ queryKey: ["storage"] });
  };
}

/** Storage used (by kind) against the plan's limit. */
export function useStorage() {
  return useQuery({
    queryKey: ["storage"],
    queryFn: () => api<StorageUsage>("/v1/me/storage"),
    staleTime: 30_000,
  });
}

/** Delete one note in Trash for good. */
export function useDeleteForever() {
  const invalidate = useInvalidateNotes();
  return useMutation({
    mutationFn: (id: string) => api(`/v1/notes/${id}?permanent=1`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
}

export function useEmptyTrash() {
  const invalidate = useInvalidateNotes();
  return useMutation({
    mutationFn: () => api<{ deleted: number }>("/v1/notes/trash/empty", { method: "POST" }),
    onSuccess: invalidate,
  });
}

/** Delete a category (the sidebar offers it only when no note uses it). */
export function useDeleteCategory() {
  const invalidate = useInvalidateNotes();
  return useMutation({
    mutationFn: (slug: string) => api(`/v1/categories/${encodeURIComponent(slug)}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
}

export function useCreateNote() {
  const invalidate = useInvalidateNotes();
  return useMutation({
    mutationFn: (input: { title: string; bodyText: string; format: NoteFormat; categories?: string[] }) =>
      api<Note>("/v1/notes", { method: "POST", body: json({ ...input, source: "web" }) }),
    onSuccess: invalidate,
  });
}

export function useUpdateNote() {
  const invalidate = useInvalidateNotes();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateNoteInput & { id: string }) =>
      api<Note>(`/v1/notes/${id}`, { method: "PATCH", body: json(input) }),
    onSuccess: invalidate,
  });
}

export function useApproveNote() {
  const invalidate = useInvalidateNotes();
  return useMutation({
    mutationFn: ({ id, allowSender }: { id: string; allowSender: boolean }) =>
      api<Note>(`/v1/notes/${id}/approve`, { method: "POST", body: json({ allowSender }) }),
    onSuccess: invalidate,
  });
}

export function useBlockSender() {
  const invalidate = useInvalidateNotes();
  return useMutation({
    mutationFn: (id: string) => api(`/v1/notes/${id}/block-sender`, { method: "POST" }),
    onSuccess: invalidate,
  });
}

export function useSenders() {
  return useQuery({
    queryKey: ["senders"],
    queryFn: async () => (await api<{ senders: Sender[] }>("/v1/senders")).senders,
  });
}

export function useSenderMutations() {
  const qc = useQueryClient();
  const onSuccess = () => qc.invalidateQueries({ queryKey: ["senders"] });
  return {
    add: useMutation({
      mutationFn: (address: string) => api("/v1/senders", { method: "POST", body: json({ address }) }),
      onSuccess,
    }),
    remove: useMutation({
      mutationFn: (address: string) => api(`/v1/senders/${encodeURIComponent(address)}`, { method: "DELETE" }),
      onSuccess,
    }),
  };
}

export function useXRemarks() {
  return useQuery({
    queryKey: ["x-remarks"],
    queryFn: async () => (await api<{ remarks: XRemark[] }>("/v1/x-remarks")).remarks,
  });
}

export function useXRemarkMutations() {
  const qc = useQueryClient();
  const onSuccess = () => qc.invalidateQueries({ queryKey: ["x-remarks"] });
  return {
    save: useMutation({
      mutationFn: (r: Pick<XRemark, "xUserId" | "handle" | "name" | "remark">) =>
        api(`/v1/x-remarks/${encodeURIComponent(r.xUserId)}`, { method: "PUT", body: json({ handle: r.handle, name: r.name, remark: r.remark }) }),
      onSuccess,
    }),
    remove: useMutation({
      mutationFn: (xUserId: string) => api(`/v1/x-remarks/${encodeURIComponent(xUserId)}`, { method: "DELETE" }),
      onSuccess,
    }),
  };
}

export function useTokens() {
  return useQuery({
    queryKey: ["tokens"],
    queryFn: async () => (await api<{ tokens: ApiToken[] }>("/v1/tokens")).tokens,
  });
}

export function useTokenMutations() {
  const qc = useQueryClient();
  const onSuccess = () => qc.invalidateQueries({ queryKey: ["tokens"] });
  return {
    create: useMutation({
      mutationFn: (name: string) =>
        api<{ id: string; name: string; token: string }>("/v1/tokens", { method: "POST", body: json({ name }) }),
      onSuccess,
    }),
    revoke: useMutation({
      mutationFn: (id: string) => api(`/v1/tokens/${id}`, { method: "DELETE" }),
      onSuccess,
    }),
  };
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api("/auth/logout", { method: "POST" }),
    onSuccess: () => {
      qc.clear();
      window.location.assign("/login");
    },
  });
}

/** Delete your own account for good (the username typed back confirms it), then go to the sign-in page. */
export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (confirmUsername: string) => api("/v1/me", { method: "DELETE", body: json({ confirmUsername }) }),
    onSuccess: () => {
      qc.clear();
      window.location.assign("/login");
    },
  });
}

export interface ShareInfo {
  token: string;
  viewCount: number;
  createdAt: string;
}

export function useShare(noteId: string) {
  return useQuery({
    queryKey: ["note", noteId, "share"],
    queryFn: async () => (await api<{ share: ShareInfo | null }>(`/v1/notes/${noteId}/share`)).share,
  });
}

export function useShareMutations(noteId: string) {
  const qc = useQueryClient();
  const set = (share: ShareInfo | null) => qc.setQueryData(["note", noteId, "share"], share);
  return {
    create: useMutation({
      mutationFn: async () => (await api<{ share: ShareInfo }>(`/v1/notes/${noteId}/share`, { method: "POST" })).share,
      onSuccess: set,
    }),
    revoke: useMutation({
      mutationFn: () => api(`/v1/notes/${noteId}/share`, { method: "DELETE" }),
      onSuccess: () => set(null),
    }),
  };
}

export interface LinkedInfo {
  accounts: Array<{ provider: "x" | "wecom"; handle: string | null; linkedAt: string }>;
  x: { available: boolean; botHandle: string };
  wecom: { available: boolean; botName: string };
}

export function useLinked() {
  return useQuery({ queryKey: ["linked"], queryFn: () => api<LinkedInfo>("/v1/linked") });
}

export function useUnlink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (provider: string) => api(`/v1/linked/${provider}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["linked"] }),
  });
}

export function useWecomCode() {
  return useMutation({
    mutationFn: () => api<{ code: string; expiresAt: string; botName: string }>("/v1/linked/wecom/code", { method: "POST" }),
  });
}

export type FileKind = "all" | "pictures" | "videos" | "files";

/** Stored files across all notes (file manager), biggest or newest first, 60 at a time. */
export function useFiles(kind: FileKind, sort: "size" | "recent") {
  return useInfiniteQuery({
    queryKey: ["files", kind, sort],
    queryFn: ({ pageParam }) => api<FilesPage>(`/v1/files?kind=${kind}&sort=${sort}&offset=${pageParam}`),
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextOffset ?? undefined,
  });
}

export function useDeleteFiles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api<{ deleted: number; bytes: number }>("/v1/files/delete", { method: "POST", body: json({ ids }) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["files"] });
      void qc.invalidateQueries({ queryKey: ["storage"] });
      void qc.invalidateQueries({ queryKey: ["note"] });
    },
  });
}

export function useBilling() {
  return useQuery({ queryKey: ["billing"], queryFn: () => api<Billing>("/v1/billing") });
}

/** Start buying a pack: resolves with the wanqu.me page to go to. */
export function useBuyStorage() {
  return useMutation({
    mutationFn: (what: { packId: string } | { renewOrderId: string }) =>
      api<{ orderId: string; payUrl: string }>("/v1/billing/orders", { method: "POST", body: json(what) }),
  });
}

export function useConfirmPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { payment: string; sig: string }) =>
      api<{ order: StorageOrder; redirectStatus: string }>("/v1/billing/return", { method: "POST", body: json(input) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["storage"] });
      void qc.invalidateQueries({ queryKey: ["billing"] });
    },
  });
}
